import React from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material'
import { CAMPAIGN_ERROR_MESSAGE, CAMPAIGN_FORM_TITLE } from '~/constants/campaign'
import { CampaignInputComponent, CampaignInputComponentProps } from './CampaignInputComponent'
import { CampaignCategory, FormatVersion, LanguageType } from '~/generated/graphql'
import { ValidationErrorMessage } from '~/components/common/ValidationErrorMessage'
import { CampaignDesiredLanguageLabel } from '~/lib/types/Campaign'
import { categoryField } from '~/features/campaign_input/type'
import { getUnsupportedTagLanguageWarning } from '~/lib/sns'

export const desiredLanguagesSchemaFields = {
  desiredLanguages: z
    .array(z.enum([LanguageType.Japanese, LanguageType.English, LanguageType.Chinese]))
    .transform((value) => {
      // LANGUAGE_OPTIONSの順序でソート
      const order = [LanguageType.Japanese, LanguageType.English, LanguageType.Chinese]
      return order.filter((lang) => value.includes(lang))
    }),
  ...categoryField,
}

export const desiredLanguagesSchema = z.object(desiredLanguagesSchemaFields)

export function validateDesiredLanguages<
  T extends z.output<typeof desiredLanguagesSchema> & { category: CampaignCategory | null },
>(v: T, ctx: z.RefinementCtx) {
  // 店舗キャンペーンの場合のみ、desiredLanguagesが必須
  if (v.category === CampaignCategory.Service) {
    if (v.desiredLanguages.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CAMPAIGN_ERROR_MESSAGE.REQUIRED,
        path: ['desiredLanguages'],
      })
      return z.NEVER
    }
  }
  return v
}

const LANGUAGE_OPTIONS = [
  { value: LanguageType.Japanese, label: CampaignDesiredLanguageLabel[LanguageType.Japanese] },
  { value: LanguageType.English, label: CampaignDesiredLanguageLabel[LanguageType.English] },
  { value: LanguageType.Chinese, label: CampaignDesiredLanguageLabel[LanguageType.Chinese] },
]

type Props = CampaignInputComponentProps

export const DesiredLanguages = ({ description, tooltip }: Props) => {
  const { control, trigger } = useFormContext()
  const category = useWatch({ name: 'category' })
  const desiredLanguages = useWatch({ name: 'desiredLanguages' }) ?? []

  // サポートされていない言語の警告メッセージを取得
  const unsupportedLanguageWarning = getUnsupportedTagLanguageWarning(desiredLanguages)

  // 店舗以外では表示しない
  if (category !== CampaignCategory.Service) {
    return null
  }

  return (
    <CampaignInputComponent
      title={CAMPAIGN_FORM_TITLE.desiredLanguages}
      required={true}
      description={description}
      tooltip={tooltip}
      compatibleFormatVersions={[FormatVersion.V4Campaign]}
    >
      {unsupportedLanguageWarning && <Typography variant="caption">{unsupportedLanguageWarning}</Typography>}
      <Controller
        name="desiredLanguages"
        control={control}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <FormGroup>
            {LANGUAGE_OPTIONS.map((option, index) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    {...field}
                    checked={field.value?.includes(option.value) ?? false}
                    onChange={async (e) => {
                      const newValue = e.target.checked
                        ? [...(field.value ?? []), option.value]
                        : (field.value ?? []).filter((v: LanguageType) => v !== option.value)
                      field.onChange(newValue)

                      await trigger('desiredLanguages')
                    }}
                    ref={index === 0 ? ref : undefined}
                  />
                }
                label={option.label}
                sx={{ width: 'fit-content' }}
              />
            ))}
            <ValidationErrorMessage error={error?.message} />
          </FormGroup>
        )}
      />
    </CampaignInputComponent>
  )
}
